/* global window, Intl, URL */
import {
  LINK_TOKEN_SHAPE,
  DAY_FORMAT,
  TIME_FORMAT,
  statusOf,
  toInstant,
  formatterFor,
  resolveTimeZone,
  describeResolveFailure,
} from './proposeLink';
import { candidateZoneLabel } from './zoneLabel';

const LINK_ID_PARAM = 'linkId';
// The bearer token stays in the fragment, which browsers do not send to servers.
const BOOK_TOKEN_FRAGMENT_KEY = 't';

const BOOK_ROUTE_PATH = '/book';
const BOOK_ROUTE_SHAPE = new RegExp(`^${BOOK_ROUTE_PATH}(/|$)`, 'i');

function readParam(name, source, pathname) {
  if (!BOOK_ROUTE_SHAPE.test(pathname)) return null;
  const value = new URLSearchParams(source).get(name);
  return value && value.trim() ? value.trim() : null;
}

function getBookToken(hash = window.location.hash, pathname = window.location.pathname) {
  if (typeof hash !== 'string' || hash.charAt(0) !== '#') return null;
  const value = readParam(BOOK_TOKEN_FRAGMENT_KEY, hash.slice(1), pathname);
  return value && LINK_TOKEN_SHAPE.test(value) ? value : null;
}

function getLinkId(search = window.location.search, pathname = window.location.pathname) {
  return readParam(LINK_ID_PARAM, search, pathname);
}

function forgetBookToken() {
  try {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    return true;
  } catch (error) {
    return false;
  }
}

function toBookingView(payload) {
  const resolved = payload || {};
  const slotDurationMinutes = Number(resolved.slotDurationMinutes);
  return {
    candidateName: resolved.candidateName || '',
    recruiterName: resolved.recruiterName || '',
    jobTitle: resolved.jobTitle || '',
    slotDurationMinutes: slotDurationMinutes > 0 ? slotDurationMinutes : null,
    expiresAtUtc: toInstant(resolved.expiresAtUtc) ? resolved.expiresAtUtc : null,
    defaultTimeZone: resolveTimeZone(resolved.candidateTimeZoneId),
  };
}

const DATE_KEY_FORMAT = { year: 'numeric', month: '2-digit', day: '2-digit' };
const DAY_TAB_FORMAT = { weekday: 'short', month: 'short', day: 'numeric' };

function dateKeyIn(timeZone) {
  const format = new Intl.DateTimeFormat('en-US', { timeZone, ...DATE_KEY_FORMAT });
  return instant => {
    const parts = format.formatToParts(instant).reduce((acc, { type, value }) => ({ ...acc, [type]: value }), {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  };
}

// Grouped here rather than on the server: a zone change is then a re-render, not a request.
function toDayGroups(slots, timeZone) {
  if (!Array.isArray(slots) || !timeZone) return [];
  const dateKey = dateKeyIn(timeZone);
  const formatDay = formatterFor(timeZone, DAY_FORMAT);
  const formatTab = formatterFor(timeZone, DAY_TAB_FORMAT);
  const formatTime = formatterFor(timeZone, TIME_FORMAT);

  return slots
    .map(slot => ({ slot: slot || {}, start: toInstant((slot || {}).startUtc) }))
    .filter(({ start }) => start)
    .reduce((days, { slot, start }) => {
      const end = toInstant(slot.endUtc);
      const startZone = candidateZoneLabel(timeZone, start);
      const endZone = end && candidateZoneLabel(timeZone, end);
      const crossesZone = end && startZone !== endZone;
      let label = end ? `${formatTime(start)} - ${formatTime(end)}` : formatTime(start);
      if (crossesZone) label = `${formatTime(start)} ${startZone} - ${formatTime(end)} ${endZone}`;
      const entry = { startUtc: slot.startUtc, label, zoneLabel: startZone, crossesZone };
      const date = dateKey(start);
      const index = days.findIndex(day => day.date === date);
      if (index === -1) return [...days, { date, label: formatDay(start), shortLabel: formatTab(start),
        zoneLabel: entry.zoneLabel, slots: [entry] }];
      return days.map((day, position) => (position === index ? {
        ...day, zoneLabel: day.zoneLabel === entry.zoneLabel ? day.zoneLabel : null, slots: [...day.slots, entry],
      } : day));
    }, []).map(day => ({ ...day, slots: day.slots.map(({ crossesZone, ...entry }) => ({
      ...entry, label: !day.zoneLabel && !crossesZone ? `${entry.label} ${entry.zoneLabel}` : entry.label,
    })) }));
}

// Runtime zone lists may omit UTC or spell the candidate's zone differently.
function timeZoneChoices(...preferred) {
  const listed = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const wanted = [...preferred, resolveTimeZone(null), 'UTC'].filter(zone => zone && resolveTimeZone(zone) === zone);
  return [...new Set([...listed, ...wanted])].sort();
}

function toTimeLabel(timeZone, startUtc, endUtc) {
  const start = toInstant(startUtc);
  if (!start) return null;
  const end = toInstant(endUtc);
  const formatDay = formatterFor(timeZone, DAY_FORMAT);
  const formatTime = formatterFor(timeZone, TIME_FORMAT);
  const label = `${formatDay(start)}, ${formatTime(start)}`;
  const startZone = candidateZoneLabel(timeZone, start);
  if (!end) return `${label} (${startZone})`;
  const endZone = candidateZoneLabel(timeZone, end);
  return startZone === endZone ? `${label} - ${formatTime(end)} (${startZone})` : `${label} (${startZone}) - ${formatTime(end)} (${endZone})`;
}

// It lands in an href, so only a web address is let through.
function toMeetingLink(value) {
  if (typeof value !== 'string' || !value) return null;
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:' ? value : null;
  } catch (error) {
    return null;
  }
}

// 410 here is an active booking (D28), not a withdrawn offer, so it has its own words.
function describeLinkFailure(error) {
  const status = statusOf(error);

  if (status === 410) {
    return {
      title: 'An interview is already booked from this link',
      description:
        'Check the confirmation you were sent. Reply to the text message that brought you here if you need to change it.',
      canRetry: false,
    };
  }

  if (status === 429) {
    return {
      title: 'Too many attempts right now',
      description: 'Please wait a few minutes and try again.',
      canRetry: true,
    };
  }

  return describeResolveFailure(error);
}

function isSlotTaken(error) {
  return statusOf(error) === 409;
}

export {
  BOOK_ROUTE_PATH,
  LINK_ID_PARAM,
  BOOK_TOKEN_FRAGMENT_KEY,
  getLinkId,
  getBookToken,
  forgetBookToken,
  toBookingView,
  toDayGroups,
  timeZoneChoices,
  toTimeLabel,
  toMeetingLink,
  describeLinkFailure,
  isSlotTaken,
};
